<template>
  <div
    class="return-date-picker"
    v-click-outside="closeCalendar">
    <div class="selected-date">
      <button
        class="return-date-picker__toggle-button"
        @click="toggleCalendar">
        <span
          class="return-date-picker__display-date"
          v-if="formattedDate">
          <svg class="return-date-picker__calendar-icon">
            <use :xlink:href="`#${calendar}`" />
          </svg>
          <div class="return-date-picker__display-text">
            <span class="selected-date__label">
              {{ $t('search.returnDate') }}
            </span>
            {{ formattedDate }}
          </div>
        </span>
        <span
          class="return-date-picker__add-trip"
          v-else>
          <svg class="return-date-picker__add-icon">
            <use :xlink:href="`#${plus}`" />
          </svg>
          {{ $t('search.returnDate') }}
        </span>
      </button>
      <button
        v-if="formattedDate"
        class="return-date-picker__cancel"
        @click="cancelDate">
        <svg class="return-date-picker__cancel-icon">
          <use :xlink:href="`#${cancel}`" />
        </svg>
      </button>
    </div>

    <div
      v-if="showCalendar"
      class="return-date-picker__calendar-overlay">
      <v-date-picker
        ref="datepicker"
        v-model="selectedDate"
        @update:model-value="handleDateSelect"
        :columns="1"
        :locale="getCurrentLocale()"
        class="vc-custom"
        :min-date="minDate"
        :timezone="'UTC'"
        :initial-page="initialPage"
        :masks="{
          title: 'MMMM YYYY',
          weekdays: 'WWW',
          input: ['L', 'DD.MM.YYYY', 'DD.MM.YY'],
          dayPopover: 'WWW, MMM D, YYYY',
        }">
        <template #nav-left-button="{ move }">
          <button
            type="button"
            @click="() => move()"
            class="vc-arrow-left">
            ‹
          </button>
        </template>
        <template #nav-right-button="{ move }">
          <button
            type="button"
            @click="() => move()"
            class="vc-arrow-right">
            ›
          </button>
        </template>
      </v-date-picker>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import type { Component, Directive } from 'vue'
  import { DatePicker } from 'v-calendar'
  import { useTravelSearchStore } from '@/stores/travelSearchStore'
  import { format } from 'date-fns'
  import calendar from '@/assets/icons/calendar.svg'
  import cancel from '@/assets/icons/cancel.svg'
  import plus from '@/assets/icons/plus.svg'
  import { getCurrentLocale } from '@/i18n'
  import { formatDateShort } from '@/utils/dateFormatters'
  import { pushInteractionToDatalayer } from '@/services/gaLoggingService'

  defineOptions({
    name: 'ReturnDate',
  })

  // Cast to the generic Component type: v-calendar's DatePicker has an enormous
  // inferred type that, left intact, makes the component type too large for TS
  // to serialize (TS7056). Aliased so the template's <v-date-picker> resolves.
  const VDatePicker = DatePicker as Component

  const vClickOutside: Directive<HTMLElement, () => void> = {
    beforeMount(el, binding) {
      ;(el as any).clickOutsideEvent = (event: Event) => {
        if (!(el === event.target || el.contains(event.target as Node))) {
          binding.value()
        }
      }
      document.body.addEventListener('click', (el as any).clickOutsideEvent)
    },
    unmounted(el) {
      document.body.removeEventListener('click', (el as any).clickOutsideEvent)
    },
  }

  const travelSearchStore = useTravelSearchStore()
  const showCalendar = ref(false)
  const selectedDate = ref<Date | null>(null)
  const datepicker = ref<any>(null)

  const minDate = computed(() => {
    const today = new Date() // Use today instead of hardcoded April
    const departureDate = travelSearchStore.departureDate
      ? new Date(travelSearchStore.departureDate)
      : today
    return departureDate > today ? departureDate : today // Ensure minDate is at least today
  })

  const initialPage = computed(() => {
    let dateToUse: Date

    if (travelSearchStore.returnDate) {
      dateToUse = new Date(travelSearchStore.returnDate)
    } else if (travelSearchStore.departureDate) {
      dateToUse = new Date(travelSearchStore.departureDate)
    } else {
      dateToUse = new Date()
    }

    return {
      month: dateToUse.getMonth() + 1,
      year: dateToUse.getFullYear(),
    }
  })

  const formattedDate = computed(() => {
    if (!selectedDate.value) return ''
    return formatDateShort(selectedDate.value)
  })

  const toggleCalendar = () => {
    showCalendar.value = !showCalendar.value
  }

  const closeCalendar = () => {
    showCalendar.value = false
  }

  const handleDateSelect = (date: Date) => {
    selectedDate.value = date
    showCalendar.value = false
    travelSearchStore.setReturnDate(format(date, 'yyyy-MM-dd'))
    pushInteractionToDatalayer('add return date', { selection: format(date, 'yyyy-MM-dd') })
  }

  watch(
    () => travelSearchStore.returnDate,
    (newReturnDate) => {
      selectedDate.value = newReturnDate ? new Date(newReturnDate) : null
    },
    { immediate: true },
  )

  const cancelDate = () => {
    selectedDate.value = null
    travelSearchStore.setReturnDate(null)
  }
</script>

<style lang="scss" scoped>
  .return-date-picker {
    display: flex;
    flex-direction: column;
    position: relative;

    .selected-date {
      @include search-field-surface;
      display: flex;
      align-items: center;
      padding: 0 $search-field-padding-x;
      text-align: left;

      &__label {
        @include search-field-label;
      }
    }

    &__toggle-button {
      @include body-md(400);
      display: flex;
      flex: 1;
      min-width: 0;
      min-height: $search-field-height;
      align-items: center;
      gap: $search-field-icon-gap;
      padding: 0;
      color: inherit;
      background: transparent;
      border: none;
      cursor: pointer;
    }

    &__display-date {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: $search-field-icon-gap;
      width: 100%;
    }

    &__display-text {
      text-align: left;
      white-space: nowrap;
    }

    &__add-trip {
      @include body-md(400);
      display: flex;
      align-items: center;
      gap: $search-field-icon-gap;
      color: $clr-light-primary-900;
      white-space: nowrap;
    }

    &__add-icon {
      @include search-field-icon;
      flex-shrink: 0;
      color: $clr-primary-200;
    }

    &__calendar-icon {
      @include search-field-icon;
      flex-shrink: 0;
    }

    // Round clear chip (Figma: 24px circle on the accent background).
    &__cancel {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: rem(24);
      height: rem(24);
      margin-left: auto;
      padding: 0;
      background-color: $clr-light-primary-15;
      border: none;
      border-radius: 50%;
      cursor: pointer;
    }

    &__cancel-icon {
      width: rem(14);
      height: rem(14);
      color: $clr-primary;
    }

    &__calendar-overlay {
      position: absolute;
      top: 100%;
      z-index: 1000;
      left: 50%;
      transform: translateX(-50%);
      background-color: white;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      @media (max-width: $breakpoint-xs) {
        left: initial;
      }
    }
  }

  :deep(.vc-custom) {
    .vc-header {
      color: #2a347a;

      .vc-title {
        color: #2a347a;
        background: transparent !important;

        &:hover {
          background: transparent !important;
        }
      }

      .vc-arrow {
        background: transparent !important;
      }

      button {
        background: transparent !important;
        &:hover {
          background: transparent !important;
        }
      }
    }

    // Calendar title button (month/year)
    .vc-title-wrapper {
      button {
        background: transparent !important;
        &:hover {
          background: transparent !important;
        }
      }
    }

    // Navigation buttons
    .vc-arrow-left,
    .vc-arrow-right,
    button.vc-nav-arrow {
      width: 26px !important;
      height: 26px !important;
      background: transparent !important;
      border: none !important;
      padding: 4px !important;
      font-size: 20px !important;
      color: #2a347a !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;

      &:hover {
        background: transparent !important;
      }
    }

    // Month/year popover styles
    .vc-popover-content {
      * {
        background: white !important;
        &:hover {
          background: #f4f5f8 !important;
        }
      }

      .vc-nav-title {
        background: transparent !important;
      }

      button {
        background: transparent !important;
        &:hover {
          background: #f4f5f8 !important;
        }
      }
    }

    // Force transparent background on all navigation elements
    [class*='vc-'] {
      &[class*='-arrow'],
      &[class*='-button'],
      &[class*='-nav'],
      &[class*='-title'] {
        background: transparent !important;
        &:hover {
          background: transparent !important;
        }
      }
    }

    // Highlight styles
    .vc-highlight {
      background-color: #2a347a !important;
      border-radius: 0 !important;
      width: 32px !important;
      height: 32px !important;

      &.vc-day-content {
        color: white !important;
        background-color: #2a347a !important;
        border-radius: 0 !important;
      }
    }

    // Target all possible disabled date scenarios
    .vc-day.is-disabled,
    .vc-day.disabled,
    .vc-day[aria-disabled='true'] {
      .vc-day-content {
        background-color: #f4f5f8 !important;
        border-radius: 0 !important;
        opacity: 1 !important;
        color: #666 !important;
        cursor: not-allowed !important;
        width: 32px !important;
        height: 32px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        position: relative !important;
        margin: 0 auto !important;

        &:hover {
          background-color: #f4f5f8 !important;
          cursor: not-allowed !important;
        }
      }
    }

    // Additional specificity for disabled dates
    .vc-container .vc-wrapper .vc-weeks .vc-day.is-disabled .vc-day-content {
      background-color: #f4f5f8 !important;
      border-radius: 0 !important;
      width: 32px !important;
      height: 32px !important;
    }

    // Force disabled styles
    .vc-day-content[aria-disabled='true'],
    .vc-day-content.is-disabled {
      background-color: #f4f5f8 !important;
      border-radius: 0 !important;
      width: 32px !important;
      height: 32px !important;
      cursor: not-allowed !important;
    }
  }

  // Global styles to ensure disabled dates are styled
  :deep(.vc-day.is-disabled),
  :deep(.vc-day.disabled),
  :deep(.vc-day[aria-disabled='true']) {
    .vc-day-content {
      background-color: #f4f5f8 !important;
      border-radius: 0 !important;
      width: 32px !important;
      height: 32px !important;
      cursor: not-allowed !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      color: #666 !important;
    }
  }
</style>
